import React, { useEffect, useRef } from "react";

/**
 * Obsidian Aura background (dark, spiritual, chic)
 * - No "stars" pattern: only slow-moving aura halos + subtle grain
 * - Respects prefers-reduced-motion
 * - Fixed canvas behind everything
 */
export default function ObsidianAuraBackground({
  intensity = 1,     // 0.6..1.4
  speed = 0.22,      // 0.12..0.35
  maxDpr = 2,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    let rafId = null;

    let w = 0;
    let h = 0;
    let dpr = 1;

    const clamp = (x, a, b) => Math.max(a, Math.min(b, x));

    // Simple deterministic noise (no dependency)
    const hash = (x, y) => {
      const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
      return s - Math.floor(s);
    };

    const resize = () => {
      w = Math.max(1, window.innerWidth);
      h = Math.max(1, window.innerHeight);
      dpr = Math.min(maxDpr, window.devicePixelRatio || 1);

      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawBase = () => {
      // Deep obsidian gradient base
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0, "#05060A");
      g.addColorStop(0.55, "#060A12");
      g.addColorStop(1, "#03040A");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    };

    const drawAura = (t) => {
      // 3 halos drifting slowly, very soft
      // Colors are intentionally desaturated for "chic" spiritual feel
      const halos = [
        { x: 0.18, y: 0.22, r: 0.70, c: "rgba(75,140,255,0.12)" },
        { x: 0.78, y: 0.30, r: 0.55, c: "rgba(120,90,255,0.09)" },
        { x: 0.52, y: 1.08, r: 0.90, c: "rgba(60,170,255,0.10)" },
      ];

      for (let i = 0; i < halos.length; i++) {
        const H = halos[i];
        const driftX = Math.sin(t * (0.6 + i * 0.22)) * 0.03;
        const driftY = Math.cos(t * (0.55 + i * 0.18)) * 0.03;

        const cx = (H.x + driftX) * w;
        const cy = (H.y + driftY) * h;
        const radius = Math.max(w, h) * H.r;

        const rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        rg.addColorStop(0, H.c);
        rg.addColorStop(0.45, "rgba(80,140,255,0.03)");
        rg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = rg;
        ctx.fillRect(0, 0, w, h);
      }

      // A subtle vignette for focus
      const vg = ctx.createRadialGradient(w * 0.5, h * 0.45, 0, w * 0.5, h * 0.45, Math.max(w, h) * 0.75);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,0.42)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, w, h);
    };

    const drawGrain = () => {
      // Very light grain (avoid banding; keep it premium)
      const step = 2; // performance/quality
      const alpha = 0.035 * clamp(intensity, 0.6, 1.4);

      const imgData = ctx.getImageData(0, 0, w, h);
      const data = imgData.data;

      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const n = hash(x, y);
          const v = (n - 0.5) * 255 * alpha;

          const idx = (y * w + x) * 4;
          data[idx] = clamp(data[idx] + v, 0, 255);
          data[idx + 1] = clamp(data[idx + 1] + v, 0, 255);
          data[idx + 2] = clamp(data[idx + 2] + v, 0, 255);
          // alpha unchanged
        }
      }
      ctx.putImageData(imgData, 0, 0);
    };

    const renderStatic = () => {
      drawBase();
      drawAura(0);
      drawGrain();
    };

    const tick = (ms) => {
      const t = (ms / 1000) * speed;
      drawBase();
      drawAura(t);
      drawGrain();
      rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      resize();
      if (reduceMotion?.matches) {
        renderStatic();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    const onResize = () => {
      resize();
      if (reduceMotion?.matches) renderStatic();
    };

    window.addEventListener("resize", onResize, { passive: true });
    reduceMotion?.addEventListener?.("change", start);

    start();

    return () => {
      window.removeEventListener("resize", onResize);
      reduceMotion?.removeEventListener?.("change", start);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [intensity, speed, maxDpr]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: -3,
        width: "100%",
        height: "100%",
      }}
    />
  );
}
